/**
 * Forfeit game — shown in place of the game menu (same modal, like Settings and
 * Game Config) when the player taps Forfeit Game. Explains the two ways to forfeit,
 * then each option's outcome with its own confirm and a Back.
 */
import React from 'react'
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native'

export type ForfeitStep = 'choose' | 'resign' | 'abandon'

export function ForfeitPanel({ step, onStep, onBackToGame, onResign, onAbandon, resignPending, abandonPending, error }: {
  step: ForfeitStep
  onStep: (step: ForfeitStep) => void
  onBackToGame: () => void
  onResign: () => void
  onAbandon: () => void
  resignPending: boolean
  abandonPending: boolean
  error: unknown
}) {
  const busy = resignPending || abandonPending
  return (
    <View className="gap-3" testID="forfeit-panel">
      <Text className="text-ink font-stencil uppercase text-lg" accessibilityRole="header">Forfeit game</Text>

      {step === 'choose' && (
        <>
          <Text className="text-ink text-sm font-typed">
            There are two ways to forfeit a game. Resigning concedes: your territories turn neutral and you're out.
            Abandoning means you stop playing and an AI takes over your seat for the rest of the game. Either way, it
            counts as a loss in ranked games.
          </Text>
          <ForfeitOption label="More about resigning" onPress={() => onStep('resign')} />
          <ForfeitOption label="More about abandoning" onPress={() => onStep('abandon')} />
          <ForfeitOption label="Back to the game" quiet onPress={onBackToGame} />
        </>
      )}

      {step === 'resign' && (
        <>
          <Text className="text-ink font-stencil uppercase">Resign</Text>
          <Text className="text-ink text-sm font-typed">
            Your territories will become neutral with their current armies still on them. You won't be able to take any
            more turns this game. In a ranked game it counts as a loss.
          </Text>
          <FinalWarning />
          <ForfeitConfirm label="Yes, resign" accessibilityLabel="Confirm resign" pending={resignPending} disabled={busy} onPress={onResign} />
          <ForfeitOption label="Back" accessibilityLabel="Back to forfeit options" quiet onPress={() => onStep('choose')} />
        </>
      )}

      {step === 'abandon' && (
        <>
          <Text className="text-ink font-stencil uppercase">Abandon</Text>
          <Text className="text-ink text-sm font-typed">
            An AI takes over your seat — your territories, armies and cards — and plays the rest of the game. You won't be
            able to return to this game. In a ranked game it counts as a loss, and it's recorded as an abandoned game on
            your profile.
          </Text>
          <FinalWarning />
          <ForfeitConfirm label="Yes, abandon" accessibilityLabel="Confirm abandon" pending={abandonPending} disabled={busy} onPress={onAbandon} />
          <ForfeitOption label="Back" accessibilityLabel="Back to forfeit options" quiet onPress={() => onStep('choose')} />
        </>
      )}

      {error instanceof Error && <Text className="text-blood text-sm font-typed" accessibilityRole="alert">{error.message}</Text>}
    </View>
  )
}

function ForfeitOption({ label, accessibilityLabel, onPress, quiet = false }: {
  label: string
  accessibilityLabel?: string
  onPress: () => void
  quiet?: boolean
}) {
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole="button"
      className={`rounded-lg px-3 py-3 items-center justify-center border border-olive ${quiet ? '' : 'bg-paper-up'}`}
      style={{ minHeight: 44 }}
    >
      <Text className="text-ink font-stencil uppercase text-center">{label}</Text>
    </TouchableOpacity>
  )
}

function ForfeitConfirm({ label, accessibilityLabel, onPress, pending, disabled }: {
  label: string
  accessibilityLabel: string
  onPress: () => void
  pending: boolean
  disabled: boolean
}) {
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      accessibilityState={{ disabled, busy: pending }}
      // Theme danger colour (same as Button's `danger` variant and Sign Out).
      className="bg-blood rounded-lg py-2 items-center justify-center flex-row gap-2"
      style={{ minHeight: 44, opacity: pending ? 0.6 : 1 }}
    >
      {pending ? <ActivityIndicator size="small" color="white" /> : <Text accessible={false} className="text-paper">⚠︎</Text>}
      <Text className="text-paper font-stencil uppercase text-center tracking-wider">{label}</Text>
    </TouchableOpacity>
  )
}

/** States that the next action is final; icon + words so it never relies on colour alone. */
function FinalWarning() {
  return (
    <View className="flex-row items-center gap-2" accessibilityRole="text">
      <Text accessible={false} className="text-blood">⚠︎</Text>
      <Text className="text-blood text-sm font-typed font-semibold flex-1">This is final — it can't be undone.</Text>
    </View>
  )
}
